import { useStatusLabels } from '../lib/ticket-status-config';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import type { ToastDetail } from '../lib/toast';
import {
  STATUS_COLOR, PRIORITY_COLOR, PRIORITY_LABEL, TYPE_ICON, TYPE_LABEL, TYPE_BADGE,
  enumLabel,
} from '../lib/format';
import { Icon } from '../lib/icons';

/**
 * Uniform status chip. Pass `blocked` wherever the ticket is in scope so a
 * blocked ticket is never shown as a plain status — it renders a red
 * "Blocked" chip right after the status chip.
 */
export function StatusBadge({ status, blocked }: { status: string; blocked?: boolean }) {
  const STATUS_LABEL = useStatusLabels();
  return (
    <>
      <span className={`chip ${STATUS_COLOR[status] ?? 'bg-slate-100 text-slate-700'}`}>
        {STATUS_LABEL[status] ?? enumLabel(status)}
      </span>
      {blocked && <span className="chip bg-rose-100 text-rose-700 font-semibold">Blocked</span>}
    </>
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`chip bg-surface2 ${PRIORITY_COLOR[priority] ?? 'text-muted'}`}>
      <Icon name="chevron-up" /> {PRIORITY_LABEL[priority] ?? priority}
    </span>
  );
}

export function TypeTag({ type, legacy, custom }: { type: string; legacy?: boolean; custom?: boolean }) {
  const tag = (
    <span className={`chip ${TYPE_BADGE[type] ?? 'bg-surface2 text-muted'}`} title={TYPE_LABEL[type] ?? type}>
      <Icon name={TYPE_ICON[type] ?? 'ticket'} /> {TYPE_LABEL[type] ?? type}
    </span>
  );
  if (!legacy && !custom) return tag;
  // Always-on "Legacy" / "Custom" badges next to the type badge, driven by the
  // isLegacy / customSystem checkboxes. whitespace-nowrap keeps the chips on one
  // line (the grid's type column sizes to fit them; see COL_WIDTH.type).
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {tag}
      {legacy && <span className="chip shrink-0 bg-amber-100 text-amber-700 text-[10px]" title="Legacy (pre-Tracx) item">Legacy</span>}
      {custom && <span className="chip shrink-0 bg-purple-100 text-purple-700 text-[10px]" title="Custom system — lives in a non-Tracx system">Custom</span>}
    </span>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden="true" className={`skeleton animate rounded-lg ${className}`} />;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="empty-state">
      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-brand/10 text-brand text-xl mb-4" aria-hidden="true"><Icon name="empty" /></div>
      <div className="font-semibold text-sm text-ink">{title}</div>
      {hint && <div className="text-sm leading-relaxed mt-1.5 max-w-sm">{hint}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight leading-tight text-ink">{title}</h1>
        {subtitle && <p className="text-sm text-muted mt-1.5 leading-relaxed">{subtitle}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide, dismissible = true, expandable = false }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean;
  /** When false, backdrop click + Escape don't close it — only the X (or an
   *  explicit Cancel/Save inside) can. Use for data-entry modals so an accidental
   *  outside click never discards progress. */
  dismissible?: boolean;
  /** Show a maximize toggle that grows the dialog to (near) full-screen — for
   *  content-heavy dialogs (e.g. Create ticket) that feel cramped at the default
   *  width. Resets to the default size each time the modal re-opens. */
  expandable?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  // Capture before React mounts children with autoFocus.
  if (open && !wasOpenRef.current) restoreFocusRef.current = document.activeElement as HTMLElement | null;
  wasOpenRef.current = open;
  const prefersReducedMotion = useReducedMotion();
  // Focus the dialog and return to the launching control when it closes.
  // Keep the setup independent of onClose (most callers pass an inline function).
  useEffect(() => {
    if (!open) return;
    const previous = restoreFocusRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => {
      if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[data-atlas-modal]');
      if (dialogs[dialogs.length - 1] !== panelRef.current) return;
      if (e.key === 'Escape' && dismissible && !e.defaultPrevented) { e.preventDefault(); onClose(); }
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      // Portaled pickers manage their own focus and return to their trigger.
      if ((e.target as HTMLElement)?.closest('[data-atlas-popover]')) return;
      const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"], [contenteditable="true"]') ?? []).filter((el) => el.getClientRects().length > 0);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (!first) { e.preventDefault(); panelRef.current?.focus(); return; }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || document.activeElement === panelRef.current)) { e.preventDefault(); first.focus(); }
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissible]);
  // Always start at the default size when the dialog opens.
  useEffect(() => { if (!open) setExpanded(false); }, [open]);
  if (!open) return null;
  return (
    <div className="modal-backdrop fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8" onClick={dismissible ? onClose : undefined}>
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-atlas-modal
        initial={{ opacity: 0, y: -8, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: prefersReducedMotion || document.documentElement.classList.contains('reduce-motion') ? 0 : 0.18, ease: 'easeOut' }}
        className={`modal-panel card shadow-pop w-full flex flex-col outline-none ${
          expanded
            ? 'max-w-none mt-0 h-[calc(100dvh-4rem)]'
            : `${wide ? 'max-w-3xl' : 'max-w-lg'} sm:mt-4 sm:max-h-[calc(100dvh-6rem)]`
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-border shrink-0">
          <h3 id={titleId} className="font-semibold tracking-tight">{title}</h3>
          <div className="flex items-center gap-1">
            {expandable && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setExpanded((e) => !e)}
                title={expanded ? 'Restore default size' : 'Expand to full screen'}
                aria-label={expanded ? 'Restore default size' : 'Expand to full screen'}
              >
                <Icon name={expanded ? 'compress' : 'expand'} />
              </button>
            )}
            <button type="button" className="btn-icon" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
          </div>
        </div>
        {/* Body scrolls when content is taller than the viewport, so action
            buttons at the bottom of a modal stay reachable. flex-1/min-h-0 lets it
            fill the dialog when expanded (keeps a sticky footer at the bottom). */}
        <div className="p-5 overflow-y-auto flex-1 min-h-0">{children}</div>
      </motion.div>
    </div>
  );
}

/** Subtle page-transition wrapper (15.2: 120–180ms). */
export function FadeIn({ children }: { children: ReactNode }) {
  const reduceMotion = useReducedMotion() || document.documentElement.classList.contains('reduce-motion');
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.16, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

const TOAST_STYLE: Record<string, string> = {
  success: 'bg-emerald-600', error: 'bg-rose-600', info: 'bg-slate-700',
};

/** Global transient toasts. Listens for window 'atlas-toast' events. */
export function Toaster() {
  const [items, setItems] = useState<ToastDetail[]>([]);
  useEffect(() => {
    const onToast = (e: Event) => {
      const detail = (e as CustomEvent<ToastDetail>).detail;
      setItems((cur) => [...cur, detail]);
      setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== detail.id)), 2600);
    };
    window.addEventListener('atlas-toast', onToast);
    return () => window.removeEventListener('atlas-toast', onToast);
  }, []);
  return (
    <div role="status" aria-live="polite" aria-atomic="false" className="fixed bottom-4 right-4 left-4 sm:left-auto z-[80] flex flex-col gap-2 items-end pointer-events-none">
      <AnimatePresence>
        {items.map((t) => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className={`text-white text-sm px-4 py-3 rounded-xl shadow-pop max-w-md break-words ${TOAST_STYLE[t.kind] ?? TOAST_STYLE.info}`}>
            {t.kind === 'success' ? <Icon name="check" /> : t.kind === 'error' ? <Icon name="warning" /> : null}{t.kind === 'success' || t.kind === 'error' ? ' ' : ''}{t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

export function MiniBar({ data, max }: { data: { key: string; count: number; color?: string }[]; max?: number }) {
  const STATUS_LABEL = useStatusLabels();
  const top = max ?? Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="space-y-1.5">
      {data.map((d) => (
        <div key={d.key} className="flex items-center gap-2 text-sm">
          <div className="w-32 truncate text-muted">{STATUS_LABEL[d.key] ?? enumLabel(d.key)}</div>
          <div className="flex-1 h-2.5 rounded-full bg-surface2 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${(d.count / top) * 100}%`, background: d.color ?? 'rgb(var(--brand))' }}
            />
          </div>
          <div className="w-8 text-right tabular-nums">{d.count}</div>
        </div>
      ))}
    </div>
  );
}
